'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Splitter } from '@terra/ui-design-system';
import { FolderLibraryIcon } from '@terra/ui-icons';
import { MainContentArea } from './MainContentArea';
import { CollapsedPanelStrip } from './CollapsedPanelStrip';
import { LayoutSplitterResizeTrigger } from './LayoutSplitterResizeTrigger';
import { DomainTreePanel } from './DomainTreePanel';
import { useDomainTreeMainLayout } from '../../hooks/layout/useDomainTreeMainLayout';
import { useMainContentTabsShallow } from '../../hooks/layout/useMainContentTabsShallow';
import { useTabScopedAssetsFilters } from '../../store/tab/assetsTabScopedFilterStore';
import { useTabScopedSignalsFilters } from '../../store/tab/signalsTabScopedFilterStore';
import { useTabScopedTracesFilters } from '../../store/tab/tracesTabScopedFilterStore';
import { useTabScopedFindingsFilters } from '../../store/tab/findingsTabScopedFilterStore';
import { SharedDomainTreeProvider } from '../../contexts/SharedDomainTreeContext';
import { useArchivedLifecycleFilter } from '../../hooks/useArchivedLifecycleFilter';
import {
  DEFAULT_LIFECYCLE_STATE_FILTER,
  type LifecycleStateFilterDto,
} from '@terra/shared-dto-types';

type ContentDomainTreeChatLayoutProps = {
  children: ReactNode;
};
type ContentDomainTreeChatLayoutBodyProps = ContentDomainTreeChatLayoutProps & {
  activeTab: ReturnType<typeof useMainContentTabsShallow>['activeTab'];
  typeFilter: ReturnType<typeof useTabScopedAssetsFilters>['typeFilter'];
  lifecycleState?: LifecycleStateFilterDto;
  layout: ReturnType<typeof useDomainTreeMainLayout>;
};

function ContentDomainTreeChatLayoutBody({
  children,
  activeTab,
  typeFilter,
  lifecycleState,
  layout,
}: ContentDomainTreeChatLayoutBodyProps) {
  const t = useTranslations('designSystem.applicationAssets.filters');

  return (
    <SharedDomainTreeProvider
      activeTabType={activeTab?.type}
      assetTypeFilter={
        activeTab?.type === 'assets-list' ? typeFilter : undefined
      }
    >
      <Splitter.Root
        ref={layout.splitterRef}
        key={layout.panelKey}
        orientation="horizontal"
        h="100%"
        flex="1"
        panels={layout.panels}
        size={layout.controlledSizes}
        onResize={layout.handleResize}
        onResizeEnd={layout.handleResizeEnd}
        borderWidth="0"
        gap={0}
      >
        <Splitter.Panel
          id="domain-tree"
          borderWidth="0"
          borderRadius="none"
          alignItems="stretch"
          justifyContent="flex-start"
          className={layout.collapsedClassName}
        >
          {layout.isCollapsed ? (
            <CollapsedPanelStrip
              position="left"
              title={t('domainHierarchy')}
              icon={FolderLibraryIcon}
              onClick={layout.toggle}
            />
          ) : (
            <DomainTreePanel
              selectionMode="multiple"
              onMinimize={layout.toggle}
              shouldApplyAppLevel={activeTab?.type === 'signals-list'}
              isExecutionsTab={activeTab?.type === 'executions-list'}
              assetTypeFilter={
                activeTab?.type === 'assets-list' ? typeFilter : undefined
              }
              countsLifecycleState={lifecycleState}
            />
          )}
        </Splitter.Panel>

        <LayoutSplitterResizeTrigger id="domain-tree:main" />

        <Splitter.Panel
          id="main"
          borderWidth="0"
          borderRadius="none"
          alignItems="stretch"
          justifyContent="flex-start"
        >
          <MainContentArea>{children}</MainContentArea>
        </Splitter.Panel>
      </Splitter.Root>
    </SharedDomainTreeProvider>
  );
}

export function ContentDomainTreeChatLayout({
  children,
}: ContentDomainTreeChatLayoutProps) {
  const { activeTab } = useMainContentTabsShallow();
  const { typeFilter, lifecycleState: assetsLifecycleState } =
    useTabScopedAssetsFilters();
  const { lifecycleState: signalsLifecycleState } =
    useTabScopedSignalsFilters();
  const { lifecycleState: tracesLifecycleState } = useTabScopedTracesFilters();
  const { lifecycleState: findingsLifecycleState } =
    useTabScopedFindingsFilters();
  const layout = useDomainTreeMainLayout();
  const tabLifecycleState =
    activeTab?.type === 'signals-list'
      ? signalsLifecycleState
      : activeTab?.type === 'executions-list'
        ? tracesLifecycleState
        : activeTab?.type === 'findings-list'
          ? findingsLifecycleState
          : assetsLifecycleState;
  const { lifecycleQueryParam } = useArchivedLifecycleFilter({
    lifecycleState: tabLifecycleState ?? DEFAULT_LIFECYCLE_STATE_FILTER,
  });

  return (
    <ContentDomainTreeChatLayoutBody
      activeTab={activeTab}
      typeFilter={typeFilter}
      lifecycleState={lifecycleQueryParam}
      layout={layout}
    >
      {children}
    </ContentDomainTreeChatLayoutBody>
  );
}
